/**
 * Framework-agnostic authentication session aggregate.
 *
 * The manager owns one linearizable browser session: async operations are
 * fenced by a generation, tokens are persisted as one bounded snapshot, and
 * refresh rotation is coordinated across tabs before touching the provider.
 */

import { taggedAuthError } from "./auth-error";
import { EventBus } from "./event-bus";
import {
  accessTokensHaveSameSessionBinding,
  buildUserInfo,
  decodeAccessTokenPayload,
  decodeIdTokenPayload,
} from "./jwt";
import { createSecureRandomId } from "./random";
import { RefreshCoordinator } from "./refresh-coordinator";
import { SessionSnapshotStore, type SessionSnapshotVersion, StaleSessionSnapshotError } from "./session-snapshot";
import { createDefaultStorageAdapter, createNamespacedAdapter } from "./storage";
import type {
  AuthError,
  AuthResult,
  AuthScope,
  AuthScopeKind,
  AuthState,
  AuthStrategy,
  EngineConfig,
  LogoutOutcome,
  StartLoginOptions,
  StorageAdapter,
  TokenSet,
  Unsubscribe,
  UserInfo,
} from "./types";

const CHECK_INTERVAL_MS = 10_000;
const DEFAULT_REFRESH_BUFFER_SECONDS = 30;
const MAX_REFRESH_BUFFER_SECONDS = 3_600;
const DEFAULT_NAMESPACE = "auth";
const INITIAL_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 30_000;

class StaleAuthOperationError extends Error {
  constructor() {
    super("Authentication operation was superseded");
    this.name = "StaleAuthOperationError";
  }
}

export class AuthManager {
  private state: AuthState = { status: "initializing" };
  private readonly provider: AuthStrategy;
  private readonly storage: StorageAdapter;
  private readonly eventBus: EventBus;
  private readonly snapshotStore: SessionSnapshotStore;
  private readonly refreshCoordinator: RefreshCoordinator;
  private readonly refreshBufferSeconds: number;
  private readonly onAuthError?: (error: AuthError) => void;

  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private refreshPromise: Promise<AuthResult> | null = null;
  private destroyed = false;
  private operationGeneration = 0;
  private snapshotEpoch: string | null = null;
  private snapshotRevision = 0;
  private refreshFailureCount = 0;
  private nextRefreshAttemptAt = 0;
  private lastHandledRefreshFailure: unknown = null;

  constructor(provider: AuthStrategy, config?: EngineConfig) {
    this.provider = provider;
    this.refreshBufferSeconds = validateRefreshBuffer(config?.refreshBufferSeconds);
    this.onAuthError = config?.onAuthError;

    const namespace = config?.namespace ?? DEFAULT_NAMESPACE;
    const baseStorage = config?.storage ?? createDefaultStorageAdapter();
    this.storage = createNamespacedAdapter(baseStorage, namespace);
    this.snapshotStore = new SessionSnapshotStore(this.storage, provider);
    this.refreshCoordinator = new RefreshCoordinator(
      this.storage,
      `zero_${namespace}_refresh`,
      createCoordinatorOwner(),
    );
    this.eventBus = new EventBus(namespace, this.storage.sharing);
    this.setupCrossTabSync();
  }

  getState(): AuthState {
    return this.state;
  }

  /** Return the opaque epoch of the current persisted session. */
  getSessionEpoch(): string | null {
    return this.snapshotEpoch;
  }

  subscribe(listener: (state: AuthState) => void): Unsubscribe {
    return this.eventBus.subscribe(listener);
  }

  async init(): Promise<void> {
    this.guardDestroyed();
    if (this.operationGeneration !== 0 || this.state.status !== "initializing") return;
    const generation = this.advanceGeneration();
    const restored = this.snapshotStore.read();
    if (restored.kind !== "valid") {
      this.clearStorage(true);
      this.transitionIfCurrent(generation, { status: "unauthenticated" });
      return;
    }

    this.snapshotEpoch = restored.epoch;
    this.snapshotRevision = restored.revision;
    const { user, tokenSet } = restored.result;
    if (!this.isTokenExpired(tokenSet)) {
      this.transitionIfCurrent(generation, { status: "authenticated", user, tokenSet });
      this.startRefreshTimer();
      return;
    }
    if (!tokenSet.refreshToken) {
      this.terminateSession(generation);
      return;
    }

    this.transitionIfCurrent(generation, { status: "refreshing", user, tokenSet });
    try {
      const refreshed = await this.refreshSession(restored.result, generation);
      this.transitionIfCurrent(generation, { status: "authenticated", ...refreshed });
      this.resetRefreshBackoff();
    } catch (error) {
      this.handleRefreshFailure(error, restored.result, generation);
    }
    if (this.isCurrent(generation)) this.startRefreshTimer();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.advanceGeneration();
    this.destroyed = true;
    this.stopRefreshTimer();
    this.refreshPromise = null;
    this.eventBus.destroy();
  }

  async logout(): Promise<void> {
    this.guardDestroyed();
    const tokenSet = this.currentTokenSet();
    const epoch = this.snapshotEpoch;
    this.advanceGeneration();
    this.clearStorage();
    this.transition({ status: "unauthenticated" });
    if (epoch) this.eventBus.broadcast({ type: "auth_logout", epoch });
    this.stopRefreshTimer();

    try {
      const outcome = await this.provider.logout(tokenSet);
      if (outcome && logoutFailed(outcome)) {
        this.onAuthError?.({ kind: "logout_failed", message: formatLogoutFailure(outcome) });
      }
    } catch (error) {
      this.onAuthError?.({
        kind: "logout_failed",
        message: error instanceof Error ? error.message : "Remote logout failed",
      });
    }
  }

  /** Clear browser-held tokens without invoking remote logout endpoints. */
  clearInMemoryAuth(): void {
    this.guardDestroyed();
    const epoch = this.snapshotEpoch;
    this.advanceGeneration();
    this.clearStorage();
    this.transition({ status: "unauthenticated" });
    if (epoch) this.eventBus.broadcast({ type: "auth_logout", epoch });
    this.stopRefreshTimer();
  }

  async startLogin(options?: StartLoginOptions): Promise<void> {
    this.guardDestroyed();
    const mode = options?.mode ?? "redirect";
    if (mode === "popup" && !(this.provider.supportsPopup?.() ?? false)) {
      throw taggedAuthError({
        kind: "unsupported_mode",
        message: `Provider "${this.provider.kind}" does not support popup login`,
      });
    }
    if (mode === "iframe" && !(this.provider.supportsIframe?.() ?? false)) {
      throw taggedAuthError({
        kind: "unsupported_mode",
        message: `Provider "${this.provider.kind}" does not support iframe login`,
      });
    }

    const generation = this.advanceGeneration();
    try {
      const result = await this.provider.startLogin(options);
      if (!this.isCurrent(generation)) return;
      if (!result) return;
      this.acceptNewSession(result, generation);
    } catch (error) {
      if (!this.isCurrent(generation)) return;
      const authError = this.toAuthError(error);
      this.transition({ status: "error", error: authError, user: null });
      this.onAuthError?.(authError);
      throw error;
    }
  }

  async handleCallback(): Promise<void> {
    this.guardDestroyed();
    const generation = this.advanceGeneration();
    try {
      const result = await this.provider.handleCallback();
      if (!this.isCurrent(generation)) return;
      this.acceptNewSession(result, generation);
    } catch (error) {
      if (!this.isCurrent(generation)) return;
      const authError = this.toAuthError(error);
      this.transition({ status: "error", error: authError, user: null });
      this.onAuthError?.(authError);
      throw error;
    }
  }

  getAccessToken(): string | null {
    const tokenSet = this.currentTokenSet();
    if (!tokenSet || this.isTokenExpired(tokenSet)) return null;
    return tokenSet.accessToken;
  }

  getAuthHeaders(): Record<string, string> {
    const token = this.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async forceRefresh(): Promise<string> {
    this.guardDestroyed();
    const session = this.currentSession();
    if (!session) {
      this.clearStorage();
      this.transition({ status: "unauthenticated" });
      throw new Error("No active session to refresh");
    }
    if (!session.tokenSet.refreshToken) {
      const generation = this.operationGeneration;
      this.terminateSession(generation);
      throw new Error("No refresh token available");
    }

    const generation = this.operationGeneration;
    this.transition({ status: "refreshing", ...session });
    try {
      const refreshed = await this.refreshSession(session, generation);
      this.transitionIfCurrent(generation, { status: "authenticated", ...refreshed });
      this.resetRefreshBackoff();
      return refreshed.tokenSet.accessToken;
    } catch (error) {
      this.handleRefreshFailure(error, session, generation);
      throw error;
    }
  }

  hasPermission(permission: string): boolean {
    return isValidPermissionList([permission]) && (this.authorizedUser()?.permissions.includes(permission) ?? false);
  }

  hasRole(role: string): boolean {
    return this.authorizedUser()?.roles.includes(role) ?? false;
  }

  hasAnyPermission(...permissions: string[]): boolean {
    if (!isValidPermissionList(permissions)) return false;
    const userPermissions = this.authorizedUser()?.permissions;
    return userPermissions ? permissions.some((permission) => userPermissions.includes(permission)) : false;
  }

  hasAllPermissions(...permissions: string[]): boolean {
    if (!isValidPermissionList(permissions)) return false;
    const userPermissions = this.authorizedUser()?.permissions;
    return userPermissions ? permissions.every((permission) => userPermissions.includes(permission)) : false;
  }

  hasAllPermissionsInScope(requiredScope: AuthScope, ...permissions: string[]): boolean {
    if (!isValidAuthScope(requiredScope) || !isValidPermissionList(permissions)) return false;
    const grants = this.authorizedUser()?.authorizationGrants;
    if (!grants) return false;
    return grants.some(
      (grant) =>
        scopeCoversTarget(grant.scope, requiredScope) &&
        permissions.every((permission) => grant.permissions.includes(permission)),
    );
  }

  hasAllPermissionsInAnyScope(requiredKind: AuthScopeKind, ...permissions: string[]): boolean {
    if (!isValidAuthScopeKind(requiredKind) || !isValidPermissionList(permissions)) return false;
    const grants = this.authorizedUser()?.authorizationGrants;
    if (!grants) return false;
    return grants.some(
      (grant) =>
        scopeCovers(grant.scope.kind, requiredKind) &&
        permissions.every((permission) => grant.permissions.includes(permission)),
    );
  }

  hasAllPermissionsForResource(
    resourceScope: AuthScope,
    minimumKind: AuthScopeKind | undefined,
    ...permissions: string[]
  ): boolean {
    if (
      !isValidAuthScope(resourceScope) ||
      (minimumKind !== undefined && !isValidAuthScopeKind(minimumKind)) ||
      !isValidPermissionList(permissions)
    ) {
      return false;
    }
    const grants = this.authorizedUser()?.authorizationGrants;
    if (!grants) return false;
    return grants.some(
      (grant) =>
        (minimumKind === undefined || scopeCovers(grant.scope.kind, minimumKind)) &&
        scopeCoversTarget(grant.scope, resourceScope) &&
        permissions.every((permission) => grant.permissions.includes(permission)),
    );
  }

  hasScope(kind: AuthScopeKind): boolean {
    const scope = this.authorizedUser()?.scope;
    return scope ? scopeCovers(scope.kind, kind) : false;
  }

  private acceptNewSession(result: AuthResult, generation: number): void {
    this.ensureCurrent(generation);
    const epoch = createSessionEpoch();
    const persisted = this.snapshotStore.writeNew(result, epoch);
    this.snapshotEpoch = persisted.epoch;
    this.snapshotRevision = persisted.revision;
    this.transition({ status: "authenticated", ...persisted.result });
    this.eventBus.broadcast({ type: "auth_changed", epoch: persisted.epoch, revision: persisted.revision });
    this.resetRefreshBackoff();
    this.startRefreshTimer();
  }

  private transition(newState: AuthState): void {
    this.state = newState;
    this.eventBus.notify(newState);
  }

  private transitionIfCurrent(generation: number, state: AuthState): void {
    this.ensureCurrent(generation);
    this.transition(state);
  }

  private startRefreshTimer(): void {
    this.stopRefreshTimer();
    this.refreshTimer = setInterval(() => {
      void this.checkAndRefresh();
    }, CHECK_INTERVAL_MS);
  }

  private stopRefreshTimer(): void {
    if (this.refreshTimer === null) return;
    clearInterval(this.refreshTimer);
    this.refreshTimer = null;
  }

  private async checkAndRefresh(): Promise<void> {
    const session = this.currentSession();
    if (!session || this.refreshPromise || Date.now() < this.nextRefreshAttemptAt) return;
    const bufferMs = this.refreshBufferSeconds * 1000;
    if (Date.now() + bufferMs < session.tokenSet.expiresAt) return;
    if (!session.tokenSet.refreshToken) {
      this.terminateSession(this.operationGeneration);
      return;
    }

    const generation = this.operationGeneration;
    this.transition({ status: "refreshing", ...session });
    try {
      const refreshed = await this.refreshSession(session, generation);
      this.transitionIfCurrent(generation, { status: "authenticated", ...refreshed });
      this.resetRefreshBackoff();
    } catch (error) {
      this.handleRefreshFailure(error, session, generation);
    }
  }

  private refreshSession(session: AuthResult, generation: number): Promise<AuthResult> {
    if (this.refreshPromise) return this.refreshPromise;
    const refreshToken = session.tokenSet.refreshToken;
    if (!refreshToken) return Promise.reject(new Error("No refresh token available"));
    const expected: SessionSnapshotVersion = {
      epoch: this.snapshotEpoch ?? "",
      revision: this.snapshotRevision,
    };
    if (!expected.epoch) return Promise.reject(new StaleAuthOperationError());
    this.lastHandledRefreshFailure = null;

    const coordinated = this.refreshCoordinator.run(
      expected,
      () => this.readCrossTabRefreshResult(session, expected, generation),
      async () => {
        this.ensureCurrent(generation);
        const tokenSet = await this.provider.refreshToken(refreshToken);
        this.ensureCurrent(generation);
        const user = this.rebuildUser(tokenSet, session);
        return { user, tokenSet };
      },
      (result) => {
        this.ensureCurrent(generation);
        const persisted = this.snapshotStore.write(result, expected);
        this.snapshotEpoch = persisted.epoch;
        this.snapshotRevision = persisted.revision;
        this.eventBus.broadcast({ type: "auth_changed", epoch: persisted.epoch, revision: persisted.revision });
        return persisted.result;
      },
    );
    const tracked = coordinated.finally(() => {
      if (this.refreshPromise === tracked) this.refreshPromise = null;
    });
    this.refreshPromise = tracked;
    return tracked;
  }

  private readCrossTabRefreshResult(
    previous: AuthResult,
    expected: SessionSnapshotVersion,
    generation: number,
  ): AuthResult | null {
    this.ensureCurrent(generation);
    const snapshot = this.snapshotStore.read();
    if (snapshot.kind !== "valid") throw new StaleAuthOperationError();
    if (snapshot.epoch !== expected.epoch) throw new StaleAuthOperationError();
    if (snapshot.revision === expected.revision) return null;
    if (
      snapshot.revision < expected.revision ||
      !tokenSetsHaveSameSessionBinding(previous.tokenSet, snapshot.result.tokenSet)
    ) {
      throw new StaleAuthOperationError();
    }
    this.snapshotEpoch = snapshot.epoch;
    this.snapshotRevision = snapshot.revision;
    return snapshot.result;
  }

  private rebuildUser(tokenSet: TokenSet, previous: AuthResult): UserInfo {
    const validated = this.provider.validateTokenSet(tokenSet);
    const previousAccessPayload = decodeAccessTokenPayload(previous.tokenSet.accessToken);
    const accessPayload = decodeAccessTokenPayload(validated.accessToken);
    const idPayload = validated.idToken ? decodeIdTokenPayload(validated.idToken) : null;
    if (!previousAccessPayload || !accessPayload || (validated.idToken && !idPayload)) {
      throw new Error("Cannot rebuild user: refreshed token claims are invalid");
    }
    if (!accessTokensHaveSameSessionBinding(previousAccessPayload, accessPayload)) {
      throw new Error("Cannot rebuild user: refreshed token session binding is invalid");
    }
    const fromJwt = buildUserInfo(idPayload, accessPayload);
    if (!fromJwt) throw new Error("Cannot rebuild user: refreshed token claims are invalid");
    return {
      ...fromJwt,
      email: fromJwt.email ?? previous.user.email,
      name: fromJwt.name ?? previous.user.name,
      picture: fromJwt.picture ?? previous.user.picture,
    };
  }

  private handleRefreshFailure(error: unknown, session: AuthResult, generation: number): void {
    if (Object.is(this.lastHandledRefreshFailure, error)) return;
    this.lastHandledRefreshFailure = error;
    if (!this.isCurrent(generation)) return;
    if (error instanceof StaleAuthOperationError || error instanceof StaleSessionSnapshotError) {
      this.synchronizeFromStorage();
      return;
    }

    const authError = this.toRefreshError(error);
    this.onAuthError?.(authError);
    if (authError.kind !== "network_error") {
      this.terminateSession(generation);
      return;
    }

    this.refreshFailureCount += 1;
    const exponent = Math.min(this.refreshFailureCount - 1, 8);
    this.nextRefreshAttemptAt = Date.now() + Math.min(INITIAL_RETRY_DELAY_MS * 2 ** exponent, MAX_RETRY_DELAY_MS);
    if (this.isTokenExpired(session.tokenSet)) {
      this.transition({ status: "refreshing", ...session });
    } else {
      this.transition({ status: "authenticated", ...session });
    }
  }

  private resetRefreshBackoff(): void {
    this.refreshFailureCount = 0;
    this.nextRefreshAttemptAt = 0;
    this.lastHandledRefreshFailure = null;
  }

  private terminateSession(generation: number): void {
    if (!this.isCurrent(generation)) return;
    const epoch = this.snapshotEpoch;
    this.advanceGeneration();
    this.clearStorage();
    this.transition({ status: "unauthenticated" });
    if (epoch) this.eventBus.broadcast({ type: "auth_logout", epoch });
    this.stopRefreshTimer();
  }

  private synchronizeFromStorage(): void {
    const generation = this.advanceGeneration();
    const snapshot = this.snapshotStore.read();
    if (snapshot.kind !== "valid") {
      this.clearStorage();
      this.transition({ status: "unauthenticated" });
      this.stopRefreshTimer();
      return;
    }
    this.snapshotEpoch = snapshot.epoch;
    this.snapshotRevision = snapshot.revision;
    const status = this.isTokenExpired(snapshot.result.tokenSet) ? "refreshing" : "authenticated";
    this.transition({ status, ...snapshot.result });
    this.startRefreshTimer();
    if (status === "refreshing") void this.checkAndRefresh();
    this.ensureCurrent(generation);
  }

  private clearStorage(unconditional = false): void {
    const epoch = this.snapshotEpoch;
    if (unconditional) {
      this.snapshotStore.clear();
    } else if (epoch) {
      this.snapshotStore.clear(epoch);
    }
    if (epoch) this.refreshCoordinator.clear(epoch);
    this.snapshotRevision = 0;
    this.snapshotEpoch = null;
    this.refreshPromise = null;
  }

  private currentSession(): AuthResult | null {
    if (this.state.status !== "authenticated" && this.state.status !== "refreshing") return null;
    return { user: this.state.user, tokenSet: this.state.tokenSet };
  }

  private currentTokenSet(): TokenSet | null {
    return this.currentSession()?.tokenSet ?? null;
  }

  private authorizedUser(): UserInfo | null {
    const session = this.currentSession();
    return session && !this.isTokenExpired(session.tokenSet) ? session.user : null;
  }

  private isTokenExpired(tokenSet: TokenSet): boolean {
    return Date.now() >= tokenSet.expiresAt;
  }

  private toAuthError(error: unknown): AuthError {
    const typed = extractAuthError(error);
    if (typed) return typed;
    return { kind: "login_failed", message: error instanceof Error ? error.message : String(error) };
  }

  private toRefreshError(error: unknown): AuthError {
    const typed = extractAuthError(error);
    if (typed?.kind === "network_error" || typed?.kind === "refresh_failed") return typed;
    return { kind: "refresh_failed", message: error instanceof Error ? error.message : String(error) };
  }

  private guardDestroyed(): void {
    if (this.destroyed) throw new Error("AuthManager has been destroyed");
  }

  private advanceGeneration(): number {
    if (this.operationGeneration >= Number.MAX_SAFE_INTEGER) {
      throw new Error("Authentication operation generation exhausted");
    }
    this.operationGeneration += 1;
    return this.operationGeneration;
  }

  private ensureCurrent(generation: number): void {
    if (!this.isCurrent(generation)) throw new StaleAuthOperationError();
  }

  private isCurrent(generation: number): boolean {
    return !this.destroyed && generation === this.operationGeneration;
  }

  private setupCrossTabSync(): void {
    this.eventBus.onRemoteMessage((event) => {
      if (this.destroyed) return;
      if (event.type === "auth_logout") {
        if (this.snapshotEpoch !== event.epoch) return;
        this.advanceGeneration();
        this.clearStorage();
        this.transition({ status: "unauthenticated" });
        this.stopRefreshTimer();
        return;
      }
      if (this.refreshPromise) return;
      const snapshot = this.snapshotStore.read();
      if (snapshot.kind !== "valid" || snapshot.epoch !== event.epoch || snapshot.revision < event.revision) return;
      this.synchronizeFromStorage();
    });
  }
}

/** Return whether a refresh failure is transport-retryable and must not log out a valid session. */
export function isRetryableAuthError(error: unknown): boolean {
  return extractAuthError(error)?.kind === "network_error";
}

function validateRefreshBuffer(value: number | undefined): number {
  const resolved = value ?? DEFAULT_REFRESH_BUFFER_SECONDS;
  if (!Number.isSafeInteger(resolved) || resolved < 0 || resolved > MAX_REFRESH_BUFFER_SECONDS) {
    throw new RangeError(`refreshBufferSeconds must be an integer between 0 and ${MAX_REFRESH_BUFFER_SECONDS}`);
  }
  return resolved;
}

function scopeCovers(granted: AuthScopeKind, required: AuthScopeKind): boolean {
  if (required === "platform") return granted === "platform";
  if (required === "tenant") return granted === "platform" || granted === "tenant";
  return true;
}

function scopeCoversTarget(granted: AuthScope, required: AuthScope): boolean {
  if (granted.kind === "platform") return true;
  if (granted.kind === "tenant") {
    return required.kind === "tenant"
      ? granted.targetId === required.targetId
      : required.kind === "realm" && required.tenantId === granted.targetId;
  }
  return required.kind === "realm" && granted.targetId === required.targetId;
}

function isValidAuthScope(scope: AuthScope): boolean {
  if (!isValidAuthScopeKind(scope.kind) || !isBoundedIdentifier(scope.targetId)) return false;
  return scope.kind !== "realm" || scope.tenantId === undefined || isBoundedIdentifier(scope.tenantId);
}

function isValidAuthScopeKind(kind: unknown): kind is AuthScopeKind {
  return kind === "platform" || kind === "tenant" || kind === "realm";
}

function isValidPermissionList(permissions: readonly string[]): boolean {
  return (
    permissions.length > 0 &&
    permissions.length <= 64 &&
    permissions.every(
      (permission) =>
        typeof permission === "string" &&
        permission.length > 0 &&
        permission.length <= 256 &&
        /^[A-Za-z0-9_.:-]+$/.test(permission),
    )
  );
}

function isBoundedIdentifier(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 128 && /^[A-Za-z0-9_-]+$/.test(value);
}

function tokenSetsHaveSameSessionBinding(previous: TokenSet, next: TokenSet): boolean {
  const previousPayload = decodeAccessTokenPayload(previous.accessToken);
  const nextPayload = decodeAccessTokenPayload(next.accessToken);
  return Boolean(previousPayload && nextPayload && accessTokensHaveSameSessionBinding(previousPayload, nextPayload));
}

function extractAuthError(error: unknown): AuthError | null {
  if (typeof error !== "object" || error === null || !("authError" in error)) return null;
  const authError = error.authError;
  if (typeof authError !== "object" || authError === null || !("kind" in authError)) return null;
  return authError as AuthError;
}

function logoutFailed(outcome: LogoutOutcome): boolean {
  return outcome.revoke === "failed" || outcome.endSession === "failed";
}

function formatLogoutFailure(outcome: LogoutOutcome): string {
  return `Remote logout incomplete (revoke=${outcome.revoke}, endSession=${outcome.endSession})`;
}

function createCoordinatorOwner(): string {
  return createSecureRandomId("auth-manager-");
}

function createSessionEpoch(): string {
  return createCoordinatorOwner();
}
